import React, {useRef, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {useIcrSubscription} from '../../firebase/firestore/useIcrSubscription';
import {createReport} from '../../firebase/firestore/reports';
import {getQueryKeywords} from './CreateReportButton';

import {useNavigate} from 'react-router';
import {parseDocx} from '../../utils/api/docxImport';
import {startReferenceImport} from '../../utils/api/referenceImport';
import useImportedContentStore from '../../stores/useImportedContentStore';
import {
    CONFIDENCE_OPTIONS,
    IFC_OPTIONS,
    ORIGINATOR_OPTIONS,
    PRODUCTION_CENTER_OPTIONS,
} from '../../components/prosemirror-editor/components/documentHeader/headerFieldOptions';
import {COMPARTMENT_OPTIONS, DISTRIBUTION_OPTIONS} from '../../components/prosemirror-editor/helpers/classificationOptions';

/**
 * Find the best matching option object for a raw text value.
 * Priority: exact value match → value prefix match → label prefix match → label contains.
 */
function findOptionMatch(options, rawText) {
    if (!rawText) return null;
    const lower = rawText.toLowerCase().trim();
    // 1. Exact value (case-insensitive)
    let m = options.find(o => o.value && o.value.toLowerCase() === lower);
    if (m) return m;
    // 2. Raw text starts with option value (e.g. "IFC1012 - …" → value "IFC1012")
    m = options.find(o => o.value && lower.startsWith(o.value.toLowerCase()));
    if (m) return m;
    // 3. Option label starts with raw text  (e.g. raw "Moderate" → label "Moderate")
    m = options.find(o => o.label && o.label.toLowerCase().startsWith(lower));
    if (m) return m;
    // 4. Option label contains raw text
    m = options.find(o => o.label && o.label.toLowerCase().includes(lower));
    if (m) return m;
    return null;
}

/**
 * Parse a comma/semicolon-separated raw string and return all matching option objects.
 */
function findMultiOptionMatches(options, rawText) {
    if (!rawText) return [];
    return rawText
        .split(/[,;]+/)
        .map(s => s.trim())
        .filter(Boolean)
        .map(part => findOptionMatch(options, part))
        .filter(Boolean)
        .filter(o => o.value); // exclude placeholder empty-value entries
}

const CLASSIFICATION_LABEL = {
    U: 'U - Unclassified',
    C: 'C - Confidential',
    S: 'S - Secret',
    TS: 'TS - Top Secret',
};

const CLASSIFICATION_COLOR = {
    U: 'default',
    C: 'info',
    S: 'warning',
    TS: 'error',
};

const ImportReportButton = ({user, icrList: icrListProp, icrLoading: icrLoadingProp, icrError: icrErrorProp}) => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [parsing, setParsing] = useState(false);
    const [parseError, setParseError] = useState(null);

    // Parsed data from backend
    const [parsedContent, setParsedContent] = useState(null);
    const [parsedReferences, setParsedReferences] = useState([]);
    const [overallClassification, setOverallClassification] = useState(null);
    const [parsedMeta, setParsedMeta] = useState({});
    // Backend-derived topic/query suggestions (used as defaults when the user doesn't set a topic).
    const [suggestedTopic, setSuggestedTopic] = useState('');
    const [suggestedQuery, setSuggestedQuery] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const [fileName, setFileName] = useState('');

    // Form state
    const [title, setTitle] = useState('');
    const [selectedIcr, setSelectedICR] = useState('');
    const [topic, setTopic] = useState('');

    // ICR list is provided by the parent (DraftsPage) to avoid duplicate fetches.
    // Fall back to fetching internally only when no list prop is supplied.
    const hasIcrProp = icrListProp !== undefined;
    const {data: icrListFetched, loading: icrLoadingFetched, error: icrErrorFetched} =
        useIcrSubscription({limit: 500, skip: hasIcrProp});
    const icrList = hasIcrProp ? icrListProp : icrListFetched;
    const icrLoading = hasIcrProp ? icrLoadingProp : icrLoadingFetched;
    const icrError = hasIcrProp ? icrErrorProp : icrErrorFetched;

    const handleOpen = () => setOpen(true);

    const handleClose = () => {
        if (submitting || parsing) return;
        setOpen(false);
        resetState();
    };

    const resetState = () => {
        setParsedContent(null);
        setParsedReferences([]);
        setOverallClassification(null);
        setParsedMeta({});
        setSuggestedTopic('');
        setSuggestedQuery('');
        setTitle('');
        setSelectedICR('');
        setTopic('');
        setFileName('');
        setParseError(null);
        setDragOver(false);
    };

    const handleFile = async (file) => {
        if (!file) return;
        if (!file.name.toLowerCase().endsWith('.docx')) {
            setParseError('Only .docx files are supported');
            return;
        }

        setFileName(file.name);
        setParsedContent(null);
        setParseError(null);
        setParsing(true);

        try {
            const result = await parseDocx(file);
            setParsedContent(result.content);
            // Only keep references that have a URL (backend already filters, but be safe)
            setParsedReferences((result.references || []).filter(r => r.url));
            setOverallClassification(result.overallClassification || 'U');
            setParsedMeta({
                dtgRaw: result.dtgRaw || '',
                originatorRaw: result.originatorRaw || '',
                distributionRaw: result.distributionRaw || '',
                compartmentRaw: result.compartmentRaw || '',
                confidenceRaw: result.confidenceRaw || '',
                ifcRaw: result.ifcRaw || '',
                productionCenterRaw: result.productionCenterRaw || '',
            });
            if (result.title) {
                setTitle(result.title);
            }
            // Backend-derived topic + query. Pre-fill the Topic field with the suggested topic so
            // the user sees (and can edit) what the search query will be generated from.
            const backendTopic = (result.suggestedTopic || '').trim();
            const backendQuery = (result.suggestedQuery || '').trim();
            setSuggestedTopic(backendTopic);
            setSuggestedQuery(backendQuery);
            if (backendTopic) {
                setTopic(backendTopic);
            }
        } catch (e) {
            setParseError(e.message || 'Failed to parse document');
        } finally {
            setParsing(false);
        }
    };

    const handleFileInputChange = (e) => {
        const file = e.target.files?.[0];
        if (file) handleFile(file);
        // Reset input so selecting the same file again fires onChange
        e.target.value = '';
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFile(file);
    };

    const handleSelectICR = (icrNumber) => {
        setSelectedICR(icrNumber);
        if (!icrNumber) {
            setTopic('');
            return;
        }
        const icr = (icrList || []).find(i => i.icrNumber === icrNumber);
        if (!icr) return;
        const parts = [icr.subject, icr.informationRequirement, icr.eei?.what, icr.eei?.where];
        setTopic(parts.filter(Boolean).join(' '));
    };

    const canSubmit = !!parsedContent && !!title.trim() && (!!selectedIcr || !!topic.trim()) && !submitting;

    const handleSubmit = async () => {
        if (!user || !canSubmit) return;

        try {
            setSubmitting(true);

            const classificationValue = overallClassification || 'U';
            const classificationLabel = CLASSIFICATION_LABEL[classificationValue] || classificationValue;

            // Match raw metadata strings to typed option objects
            const validOptions = arr => arr.filter(o => o.value);
            const originatorMatch = findOptionMatch(validOptions(ORIGINATOR_OPTIONS), parsedMeta.originatorRaw);
            const confidenceMatch = findOptionMatch(validOptions(CONFIDENCE_OPTIONS), parsedMeta.confidenceRaw);
            const ifcMatch = findOptionMatch(validOptions(IFC_OPTIONS), parsedMeta.ifcRaw);
            const productionCenterMatch = findOptionMatch(validOptions(PRODUCTION_CENTER_OPTIONS), parsedMeta.productionCenterRaw);
            const compartmentMatches = findMultiOptionMatches(COMPARTMENT_OPTIONS, parsedMeta.compartmentRaw);
            const distributionMatches = findMultiOptionMatches(DISTRIBUTION_OPTIONS, parsedMeta.distributionRaw);

            // Resolve the effective topic: user-entered topic, else backend suggestion, else the
            // cleaned title. We always persist a NON-EMPTY topic so the report never falls back to
            // seeding the Sources search box from the raw report name on load.
            const cleanedTitle = title.trim().replace(/\s*\(Imported from DocX\)\s*$/i, '').trim();
            const topicText = topic.trim() || suggestedTopic || cleanedTitle;

            // Reuse the backend-generated query when the topic is unchanged from the suggestion;
            // otherwise regenerate from the effective topic so an edited topic stays in sync.
            let defaultQuery = '';
            if (topicText) {
                if (suggestedQuery && topicText === suggestedTopic) {
                    defaultQuery = suggestedQuery;
                } else {
                    defaultQuery = await getQueryKeywords(topicText);
                }
            }

            const payload = {
                title: title.trim(),
                topic: topicText,
                defaultQuery,
                productType: {value: 'analytic', label: 'Analytic Product'},
                userId: user.uid,
                userDisplayName: user.displayName || 'Anonymous',
                status: 'Draft',
                isImported: true,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                meta: {
                    title: title.trim(),
                    ...(selectedIcr ? {icrNumber: selectedIcr} : {}),
                    productType: {value: 'analytic', label: 'Analytic Product'},
                    dtg: parsedMeta.dtgRaw || '',
                    originator: originatorMatch || {value: '', label: 'Select originator'},
                    classification: {value: classificationValue, label: classificationLabel},
                    distribution: distributionMatches,
                    compartment: compartmentMatches,
                    confidence: confidenceMatch || {value: '', label: 'Select confidence'},
                    ifc: ifcMatch || {value: '', label: 'Select IFC'},
                    productionCenter: productionCenterMatch || {value: '', label: 'Select production center'},
                    authors: [],
                },
            };

            const {reportId} = await createReport(payload);

            // Store parsed content and references so the editor can inject them on mount
            useImportedContentStore.getState().setImportedContent(reportId, parsedContent, parsedReferences);

            // Kick off async reference import if there are references with URLs
            if (parsedReferences.length > 0) {
                useImportedContentStore.getState().setReferenceImportStatus('processing');
                startReferenceImport(reportId, parsedReferences).catch(err => {
                    console.error('[ImportReport] Failed to start reference import:', err);
                    useImportedContentStore.getState().setReferenceImportStatus('failed');
                });
            } else {
                // no references with URLs — reference import skipped
            }

            setOpen(false);
            resetState();
            const queryString = defaultQuery
                ? `?query=${encodeURIComponent(defaultQuery)}&aiSearch=`
                : '';
            navigate(`/products/${reportId}/edit${queryString}`);
        } catch (e) {
            console.error('Failed to import report', e);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Button
                variant="outlined"
                startIcon={<UploadFileIcon/>}
                onClick={handleOpen}
                disabled={submitting}
            >
                Import Report
            </Button>

            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>Import Report from DOCX</DialogTitle>
                <DialogContent>
                    <Stack mt={1} spacing={2}>

                        {/* Drop zone */}
                        <Box
                            onDragOver={(e) => {
                                e.preventDefault();
                                setDragOver(true);
                            }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{
                                border: '2px dashed',
                                borderColor: dragOver ? 'primary.main' : 'divider',
                                borderRadius: 1,
                                p: 3,
                                textAlign: 'center',
                                cursor: 'pointer',
                                bgcolor: dragOver ? 'action.hover' : 'transparent',
                                transition: 'border-color 0.2s, background-color 0.2s',
                                '&:hover': {bgcolor: 'action.hover'},
                            }}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".docx"
                                style={{display: 'none'}}
                                onChange={handleFileInputChange}
                            />
                            {parsing ? (
                                <Stack alignItems="center" spacing={1}>
                                    <CircularProgress size={24}/>
                                    <Typography variant="body2" color="text.secondary">
                                        Parsing document…
                                    </Typography>
                                </Stack>
                            ) : fileName ? (
                                <Stack alignItems="center" spacing={1}>
                                    <UploadFileIcon color="primary"/>
                                    <Typography variant="body2" color="text.primary" fontWeight={500}>
                                        {fileName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Click to replace
                                    </Typography>
                                </Stack>
                            ) : (
                                <Stack alignItems="center" spacing={1}>
                                    <UploadFileIcon color="action"/>
                                    <Typography variant="body2" color="text.secondary">
                                        Drop a <strong>.docx</strong> file here, or click to browse
                                    </Typography>
                                </Stack>
                            )}
                        </Box>

                        {parseError && (
                            <Typography variant="caption" color="error">
                                {parseError}
                            </Typography>
                        )}

                        {/* Overall classification badge */}
                        {overallClassification && (
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="caption" color="text.secondary">
                                    Document classification:
                                </Typography>
                                <Chip
                                    size="small"
                                    label={CLASSIFICATION_LABEL[overallClassification] || overallClassification}
                                    color={CLASSIFICATION_COLOR[overallClassification] || 'default'}
                                    variant="outlined"
                                />
                            </Stack>
                        )}

                        {/* Title */}
                        <TextField
                            label="Title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            fullWidth
                            disabled={submitting}
                            required
                        />

                        <FormControl fullWidth>
                            <InputLabel id="import-icr-label">Select ICR (optional)</InputLabel>
                            <Select
                                labelId="import-icr-label"
                                label="Select ICR (optional)"
                                variant="outlined"
                                value={selectedIcr}
                                onChange={(e) => handleSelectICR(e.target.value)}
                                disabled={submitting || icrLoading}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {(icrList || []).map(icr => (
                                    <MenuItem key={icr.icrNumber} value={icr.icrNumber}>
                                        <Stack>
                                            <Typography>{icr.subject}</Typography>
                                            <Typography variant="caption"
                                                        color="text.secondary">{icr.icrNumber}</Typography>
                                        </Stack>
                                    </MenuItem>
                                ))}
                            </Select>
                            {icrLoading && (
                                <Typography variant="caption" color="text.secondary">Loading ICRs…</Typography>
                            )}
                            {icrError && (
                                <Typography variant="caption" color="error">Failed to load ICRs</Typography>
                            )}
                        </FormControl>

                        {/* Topic */}
                        <TextField
                            label="Topic"
                            value={topic}
                            onChange={(e) => setTopic(e.target.value)}
                            placeholder="Describe what your report is about"
                            fullWidth
                            multiline
                            minRows={2}
                            required={!selectedIcr}
                            disabled={submitting}
                            helperText={selectedIcr ? 'Optional when ICR is selected' : 'Required when no ICR is selected'}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={submitting || parsing}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={!canSubmit}
                        startIcon={submitting ? <CircularProgress size={16}/> : undefined}
                    >
                        {submitting ? 'Importing…' : 'Import'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ImportReportButton;
